import * as React from "react";
import { THEME_COOKIE_NAME, resolveInitialTheme } from "../helpers/theme";
import { applyFrameworkTheme } from "../helpers/themeAdapter";

export interface IThemeContext {
    light: boolean;
    setTheme: (light: boolean) => void;
}

const ThemeContext = React.createContext<IThemeContext | undefined>(undefined);

export const useTheme = (): IThemeContext => {
    const context = React.useContext(ThemeContext);

    if (!context) {
        throw new Error("useTheme must be used within ThemeProvider");
    }

    return context;
};

const updateThemeSideEffects = (light: boolean): void => {
    applyFrameworkTheme(light);
    if (light) {
        document.body.classList.remove("dark-theme");
        document.body.classList.add("light-theme");
        document.cookie = `${THEME_COOKIE_NAME}=light; expires=Thu, 01 Jan 9999 00:00:00 UTC; path=/;`;
    } else {
        document.body.classList.remove("light-theme");
        document.body.classList.add("dark-theme");
        document.cookie = `${THEME_COOKIE_NAME}=dark; expires=Thu, 01 Jan 9999 00:00:00 UTC; path=/;`;
    }
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [light, setTheme] = React.useState(() =>
        resolveInitialTheme(
            document.cookie,
            window.matchMedia("(prefers-color-scheme: dark)").matches,
        ),
    );

    React.useEffect(() => {
        updateThemeSideEffects(light);
    }, [light]);

    const value = React.useMemo(() => ({ light, setTheme }), [light]);

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};

export default ThemeContext;
